// Per-session run metadata for the E1 outcomes ledger (renderer-side).
// TestsTab and SessionWorkspace write; recordOutcome reads at run-end.

import { apiFetch as defaultApiFetch } from './api.js';

/** @type {Map<string, { green_pass?: 0|1|null, test_retries?: number, stopReason?: string, last_outcome_id?: string }>} */
const state = new Map();

// Loop stopReasons that mean the run failed (vs cleanly finished). Kept in sync
// with the terminal stopReasons emitted by agentLoop.runLocalAgentLoop.
const SUCCESS_FAILURE_STOPS = new Set([
  'tool_failure_spiral', 'model_error', 'exception', 'context_overflow',
  'stall_gave_up', 'no_root', 'tools_done_followup_failed',
]);

export function setRunAgentMeta(sessionId, patch = {}) {
  if (!sessionId) return;
  state.set(sessionId, { ...(state.get(sessionId) || {}), ...patch });
}

export function getRunAgentMeta(sessionId) {
  return sessionId ? (state.get(sessionId) || {}) : {};
}

export function noteTestOutcome(sessionId, { green_pass, retries = 0 } = {}) {
  if (!sessionId) return;
  setRunAgentMeta(sessionId, { green_pass, test_retries: retries });
}

export function setLastOutcomeId(sessionId, outcomeId) {
  if (!sessionId || !outcomeId) return;
  setRunAgentMeta(sessionId, { last_outcome_id: outcomeId });
}

// WS-F/F9 (incidente tastedesign) — antes TODO turno do code-IDE era gravado como
// 'fix' (literal, sem classificador), colapsando a variedade de tarefas em 1 bucket e
// cegando o computeModelTaskStats (success_rate por task_type que o roteador consome).
// Heurística pura por palavra-chave; sem texto → 'fix' (default histórico). Ordem
// importa: refactor > test > feature > plan > fix > other.
export function classifyTaskType(text) {
  const t = String(text || '').toLowerCase();
  if (!t.trim()) return 'fix';
  if (/refactor|refatora|reescrev|renomea|extrai\b/.test(t)) return 'refactor';
  if (/\b(test|tests|teste|testes|spec|specs|cobertura|coverage)\b/.test(t)) return 'test';
  if (/\b(add|adicion|implement|cria|criar|criação|build|feature|funcionalidade)/.test(t)) return 'feature';
  if (/\bplan(o|os|e|ejar)?\b/.test(t)) return 'plan';
  if (/\b(fix|bug|erro|error|quebrad|conserta|corrig|broken|falha)/.test(t)) return 'fix';
  return 'other';
}

/**
 * Build the POST /outcomes body for a finished agent run.
 * @param {string} sessionId
 * @param {{ model_id?: string, iterations?: number, stopReason?: string, reachedGreen?: boolean, text?: string, taskType?: string }} [opts]
 */
export function buildRunOutcomePayload(sessionId, { model_id, iterations = 0, stopReason, reachedGreen, text, taskType } = {}) {
  const meta = getRunAgentMeta(sessionId);
  const stop = stopReason || meta.stopReason;
  let green = meta.green_pass; // a real test result (TestsTab) is the strongest local signal — always wins
  // E1 (honest-green) — the ORACLE verdict (agentLoop runOracle → reachedGreen) is
  // the VERIFIED signal; it overrides any stopReason inference. We no longer
  // fabricate green_pass=1 from a clean loop exit ('done') alone: a finish with NO
  // verifier is NOT success, it is not_run (null). The agent reaching 'done' is a
  // self-report ("I think I'm finished"), not a verifier saying the work passed —
  // crediting it as success was the law↔signal gap this closes.
  if (green == null && typeof reachedGreen === 'boolean') green = reachedGreen ? 1 : 0;
  // Only an EXPLICIT failure stop downgrades an unverified run to 0. A clean exit
  // with no verifier stays null (not_run) — honest "we could not verify". The
  // constitution's DONE gate pushes the agent to RUN a check so fewer runs are not_run.
  if (green == null && SUCCESS_FAILURE_STOPS.has(stop)) green = 0;
  // 'done'/'done_after_budget' without a verifier, 'max_iterations'/'aborted'/
  // 'no_chat_fn' → null (not_run — neither a verified success nor a model failure).

  return {
    session_id: sessionId,
    model_id,
    // Usa taskType explícito se dado; senão classifica pelo texto da tarefa; senão 'fix'.
    task_type: taskType || classifyTaskType(text),
    green_pass: green ?? null,
    retries: Math.max(iterations || 0, meta.test_retries || 0),
  };
}

/**
 * Remember the PR number the run opened by PATCHing it onto the session's last
 * outcome row. The remote merge state (the STRONG human_merged signal) can only
 * be read back later if we know WHICH PR produced this row — local git never
 * sees a remote squash-merge. Pure-ish: deps injectable for tests.
 *
 * @param {string} sessionId
 * @param {number} prNumber
 * @param {{ apiFetch?: typeof defaultApiFetch, getMeta?: typeof getRunAgentMeta }} [deps]
 * @returns {Promise<{ ok: boolean, skipped?: boolean, error?: string }>}
 */
export async function patchOutcomePrNumber(sessionId, prNumber, { apiFetch = defaultApiFetch, getMeta = getRunAgentMeta } = {}) {
  if (!Number.isInteger(prNumber) || prNumber <= 0) return { ok: false, skipped: true };
  const outcomeId = getMeta(sessionId)?.last_outcome_id;
  if (!outcomeId) return { ok: false, skipped: true };
  try {
    const res = await apiFetch(`/api/code-ide/outcomes/${outcomeId}/signal`, {
      method: 'PATCH',
      body: { pr_number: prNumber },
    });
    return res?.ok ? { ok: true } : { ok: false, error: res?.error || `HTTP ${res?.status}` };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}

export function clearRunAgentMeta(sessionId) {
  if (sessionId) state.delete(sessionId);
}
